import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ExternalLink, CheckCircle, Store, Zap, ShoppingCart, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";
interface NuvemshopIntegrationStepProps {
  onNext: () => void;
  onPrev: () => void;
  onIntegrationSuccess?: () => void;
}
export function NuvemshopIntegrationStep({
  onNext,
  onPrev,
  onIntegrationSuccess
}: NuvemshopIntegrationStepProps) {
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const {
    updateProgress
  } = useOnboardingProgress();
  const [isIntegrating, setIsIntegrating] = useState(false);
  const [isIntegrated, setIsIntegrated] = useState(false);
  const [integrationWindow, setIntegrationWindow] = useState<Window | null>(null);

  // Verificar se já está integrado ao carregar
  useEffect(() => {
    console.log('🔍 NuvemshopIntegrationStep: Checking integration status');
    checkIntegrationStatus();
  }, [user]);

  // Auto-avançar se já estiver integrado
  useEffect(() => {
    if (isIntegrated) {
      console.log('🎯 Already integrated, auto-advancing to next step');
      // Remover o auto-advance, deixar o usuário clicar em "Continuar"
    }
  }, [isIntegrated]);

  // Escutar por mensagens de integração bem-sucedida
  useEffect(() => {
    const handleIntegrationMessage = (event: MessageEvent) => {
      console.log('📨 Integration message received:', event.data);
      if (event.data?.type === 'nuvemshop-integration-success') {
        handleIntegrationSuccess();
      }
    };
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'nuvemshop-integration-success' && event.newValue) {
        console.log('📦 Integration success from localStorage');
        handleIntegrationSuccess();
        localStorage.removeItem('nuvemshop-integration-success');
      }
    };
    window.addEventListener('message', handleIntegrationMessage);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('message', handleIntegrationMessage);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  const checkIntegrationStatus = async () => {
    if (!user) return;
    try {
      console.log('🔍 Checking integration status for user:', user.id);

      // Verificar na tabela nuvemshop_tokens
      const {
        data: tokenData,
        error: tokenError
      } = await supabase.from('nuvemshop_tokens').select('id').eq('user_id', user.id).eq('is_active', true).maybeSingle();
      if (tokenError) {
        console.error('❌ Error checking tokens:', tokenError);
      }

      // Verificar na tabela integrations
      const {
        data: integrationData,
        error: integrationError
      } = await supabase.from('integrations').select('id').eq('user_id', user.id).eq('integration_type', 'nuvemshop').eq('status', 'active').maybeSingle();
      if (integrationError) {
        console.error('❌ Error checking integrations:', integrationError);
      }
      const hasIntegration = !!(tokenData || integrationData);
      console.log('📊 Integration status:', {
        hasToken: !!tokenData,
        hasIntegration: !!integrationData,
        isIntegrated: hasIntegration
      });
      setIsIntegrated(hasIntegration);

      // Se já está integrado, atualizar o progresso
      if (hasIntegration) {
        console.log('✅ Integration found, updating progress');
        await updateProgress({
          nuvemshop_integrated: true
        });
      }
    } catch (error) {
      console.error('❌ Error checking integration status:', error);
    }
  };
  const handleIntegrationSuccess = async () => {
    console.log('🎉 Integration success handler called');
    setIsIntegrating(false);
    
    if (integrationWindow && !integrationWindow.closed) {
      integrationWindow.close();
    }

    // Re-verificar status após um pequeno delay para garantir que dados foram salvos
    setTimeout(async () => {
      await checkIntegrationStatus();
    }, 1000);

    // Atualizar progresso no banco
    try {
      console.log('💾 Updating progress: nuvemshop_integrated = true');
      await updateProgress({
        nuvemshop_integrated: true
      });
      
      toast({
        title: "Integração realizada com sucesso!",
        description: "Sua loja Nuvemshop foi conectada à plataforma."
      });

    } catch (error) {
      console.error('❌ Error updating progress:', error);
    }
  };
  const handleIntegrate = async () => {
    if (!user) return;
    setIsIntegrating(true);
    console.log('🚀 Starting integration for user:', user.id);
    try {
      const requestData = {
        action: 'start_oauth',
        userId: user.id
      };
      const {
        data,
        error
      } = await supabase.functions.invoke('nuvemshop-integration', {
        body: requestData
      });
      if (error) {
        throw new Error(error.message || 'Erro na comunicação com o servidor');
      }
      if (!data || !data.success) {
        throw new Error(data?.error || 'Falha ao iniciar OAuth');
      }
      if (!data.auth_url) {
        throw new Error('URL de autorização não foi gerada pelo servidor');
      }
      console.log('🌐 Opening popup with URL:', data.auth_url);
      const popup = window.open(data.auth_url, 'nuvemshop-auth', 'width=600,height=700,scrollbars=yes,resizable=yes');
      setIntegrationWindow(popup);
      if (!popup) {
        throw new Error('Popup foi bloqueado pelo navegador');
      }
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setIsIntegrating(false);
          console.log('🔒 Popup closed - re-checking integration status');
          // Re-verificar status quando popup for fechado
          setTimeout(() => {
            checkIntegrationStatus();
          }, 2000);
        }
      }, 1000);
    } catch (error: any) {
      console.error('❌ Integration error:', error);
      setIsIntegrating(false);
      toast({
        title: "Erro na integração",
        description: error.message || "Não foi possível iniciar a integração com a Nuvemshop",
        variant: "destructive"
      });
    }
  };
  const handleSkip = async () => {
    console.log('⏭️ Skipping Nuvemshop integration');
    toast({
      title: "Integração pulada",
      description: "Você pode configurar a integração mais tarde na página de integrações."
    });
    onNext();
  };
  const handleContinue = () => {
    console.log('▶️ Continue button clicked, isIntegrated:', isIntegrated);
    console.log('▶️ About to call onNext function');
    if (isIntegrated) {
      onNext();
      console.log('▶️ onNext called successfully');
    } else {
      console.log('❌ Cannot continue - not integrated');
    }
  };
  return <div className="space-y-4">
      <div className="text-center">
        <Store className="w-12 h-12 mx-auto mb-4 text-primary" />
        <h2 className="text-2xl font-semibold text-card-foreground mb-2">Conectar sua Loja</h2>
        <p className="text-muted-foreground">
          Conecte sua loja Nuvemshop para sincronizar pedidos e clientes automaticamente.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Store className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                Nuvemshop
                {isIntegrated && <Badge variant="default" className="bg-green-100 text-green-700 border-green-200">
                    Conectada
                  </Badge>}
              </CardTitle>
              <CardDescription>
                Plataforma de e-commerce para sincronização automática
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Zap className="w-4 h-4 text-primary" />
              <span>Sincronização automática</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ShoppingCart className="w-4 h-4 text-primary" />
              <span>Pedidos em tempo real</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-primary" />
              <span>Cashback automático</span>
            </div>
          </div>

          {isIntegrating && <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <div>
                  <p className="font-medium text-card-foreground">Aguardando autorização...</p>
                  <p className="text-sm text-muted-foreground">
                    Uma nova janela foi aberta. Faça login na sua conta Nuvemshop e autorize a integração.
                  </p>
                </div>
              </div>
            </div>}

          {isIntegrated && <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">Loja conectada com sucesso!</p>
                  <p className="text-sm text-green-700">
                    Sua loja Nuvemshop está conectada e sincronizando automaticamente.
                  </p>
                </div>
              </div>
            </div>}

          {!isIntegrated && !isIntegrating && <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Ao conectar sua loja, você permitirá que nossa plataforma:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 pl-4">
                <li>• Sincronize pedidos automaticamente</li>
                <li>• Identifique e gerencie clientes</li>
                <li>• Processe cashback em tempo real</li>
                <li>• Gere cashback de desconto</li>
              </ul>
            </div>}
        </CardContent>
      </Card>

      <div className="flex gap-20 mt-3">
        <Button variant="outline" onClick={onPrev} className="flex-1">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        
        {isIntegrated ? <Button onClick={handleContinue} className="flex-1">
            Continuar
          </Button> : <>
            
            <Button onClick={handleIntegrate} disabled={isIntegrating} className="flex-1">
              {isIntegrating ? <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Aguardando...
                </> : <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Conectar Loja
                </>}
            </Button>
          </>}
      </div>
    </div>;
}